<script lang="ts">
  import { onMount } from 'svelte';
  import Prose from '../lib/components/Prose.svelte';

  type PartialProduct = { expr: string; stated: string; actual: string; correct: boolean };
  type Trace = {
    digit: number;
    probe: number;
    operands: { a: string; b: string };
    truth: string;
    answer: string | null;
    finalCorrect: boolean;
    partialProducts: PartialProduct[];
    correctSteps: number;
    totalSteps: number;
    pStep: number;
  };
  type Data = { model: string; traces: Trace[] };

  // Cherry-picked representatives: easy / boundary / failing.
  const PICK = [
    { digit: 5, probe: 0, label: 'comfortable' },
    { digit: 10, probe: 1, label: 'at the edge' },
    { digit: 20, probe: 0, label: 'past the edge' },
  ];

  let data = $state<Data | null>(null);

  onMount(async () => {
    const res = await fetch('./mul-traces.json');
    data = await res.json();
  });

  function pick(traces: Trace[], d: number, p: number): Trace | null {
    return traces.find((t) => t.digit === d && t.probe === p) ?? null;
  }

  function formatNumberWithCommas(s: string): string {
    return BigInt(s).toLocaleString('en-US');
  }

  function scientific(n: number): string {
    if (n >= 0.01) return n.toFixed(3);
    if (n === 0) return '0';
    const exp = Math.floor(Math.log10(n));
    const mant = n / Math.pow(10, exp);
    return `${mant.toFixed(2)} × 10^${exp}`;
  }
</script>

<section id="reliability" class="section">
  <Prose>
    <header class="header">
      <div class="eyebrow">Framework</div>
      <h2>Reliability is step accuracy</h2>
    </header>

    <p>
      Every model output is learned recall. The outline of an algorithm — which
      operations happen, in what order, and how data moves between them — is a
      pattern stored in the weights. The operations inside that outline are stored
      the same way: arithmetic facts, lookups, transformations, and state updates.
      At the parameter level, they are all just steps with some probability of
      being emitted correctly.
    </p>

    <p>
      Long multiplication makes the idea concrete. The procedure is everywhere in
      training data, and so are the single-digit facts it depends on. Facts like
      <em>7 × 8 = 56</em> are densely represented. At inference time, the model is
      not deriving a proof of multiplication; it is emitting the pattern training
      made likely.
    </p>
  </Prose>

  <div class="training-origins" aria-label="Algorithm shape and arithmetic operations both come from training">
    <div class="origin-card">
      <div class="origin-label">Both come from training</div>

      <div class="tableau">
        <div class="tab-row">       54122</div>
        <div class="tab-row"><span class="scaffold">×</span>      38804</div>
        <div class="tab-row scaffold">────────────</div>
        <div class="tab-row"><span class="value">      216488</span>   <span class="scaffold">←</span> 54122 × <span class="value">4</span></div>
        <div class="tab-row"><span class="value">    43297600</span>   <span class="scaffold">←</span> 54122 × <span class="value">800</span></div>
        <div class="tab-row"><span class="value">   432976000</span>   <span class="scaffold">←</span> 54122 × <span class="value">8000</span></div>
        <div class="tab-row"><span class="value">  1623660000</span>   <span class="scaffold">←</span> 54122 × <span class="value">30000</span></div>
        <div class="tab-row scaffold">────────────</div>
        <div class="tab-row"><span class="value">  2100150088</span></div>
      </div>

      <div class="dual-caption">
        <div class="dual-item">
          <span class="dual-swatch scaffold-swatch"></span>
          <span class="dual-key">Shape</span>
          <span class="dual-val">align, multiply, shift, sum — the learned procedure.</span>
        </div>
        <div class="dual-item">
          <span class="dual-swatch value-swatch"></span>
          <span class="dual-key">Operations</span>
          <span class="dual-val">partial products and the final addition — learned facts.</span>
        </div>
        <div class="dual-note">Both are recall. Both contribute steps to <em>p<sub>step</sub><sup>N</sup></em>.</div>
      </div>
    </div>
  </div>

  <Prose>
    <p>
      The setup works while the problem stays close to what the model has seen.
      When the exact instance is new, the learned procedure reduces it to smaller
      pieces the model does know. A 12-digit multiplication becomes roughly 144
      single-digit products, plus carries and alignment. Each piece is familiar,
      but the chain is now long enough that one miss can spoil the run.
    </p>

    <h3 class="subhead">How the chain collapses</h3>
    <p>
      Increase one variable — operand size — and the rest follows: more steps,
      lower per-step accuracy, lower end-to-end reliability.
    </p>
  </Prose>

  <div class="panels">
    {#if data}
      {#each PICK as { digit, probe, label } (digit + ':' + probe)}
        {@const trace = pick(data.traces, digit, probe)}
        {#if trace}
          {@const predicted = Math.pow(trace.pStep, trace.totalSteps)}
          <div class="panel">
            <div class="panel-header">
              <div class="size">{digit}-digit × {digit}-digit</div>
              <div class="regime">{label}</div>
            </div>
            <div class="problem">
              <div class="operands">
                <span>{formatNumberWithCommas(trace.operands.a)}</span>
                <span class="times">×</span>
                <span>{formatNumberWithCommas(trace.operands.b)}</span>
              </div>
            </div>
            <div class="grid" aria-label={`${trace.totalSteps} partial products, ${trace.correctSteps} correct`}>
              {#each trace.partialProducts as pp, i (i)}
                <span class="dot" class:ok={pp.correct} class:err={!pp.correct}
                      title={`${pp.expr} = ${pp.stated}${pp.correct ? ' ✓' : ` ✗ (actual ${pp.actual})`}`}></span>
              {/each}
            </div>
            <div class="stats">
              <div class="stat">
                <span class="k">N</span>
                <span class="v">{trace.totalSteps}</span>
                <span class="d">observed steps</span>
              </div>
              <div class="stat">
                <span class="k">p<sub>step</sub></span>
                <span class="v">{trace.pStep.toFixed(3)}</span>
                <span class="d">{trace.correctSteps} / {trace.totalSteps} correct</span>
              </div>
              <div class="stat">
                <span class="k">p<sub>step</sub><sup>N</sup></span>
                <span class="v">{scientific(predicted)}</span>
                <span class="d">predicted success</span>
              </div>
            </div>
            <div class="outcome" class:ok={trace.finalCorrect} class:err={!trace.finalCorrect}>
              {#if trace.finalCorrect}
                ✓ Haiku answered correctly
              {:else}
                ✗ Haiku answered incorrectly
              {/if}
            </div>
          </div>
        {/if}
      {/each}
    {/if}
  </div>

  <Prose>
    <p>
      The hard case did not require a new algorithm. It required the same learned
      operation to fire cleanly many more times. Eventually one step missed, and
      the remaining chain inherited the damage.
    </p>
  </Prose>
</section>

<style>
  .section {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: var(--space-4xl);
    width: 100%;
  }
  .header {
    margin-bottom: var(--space-lg);
  }
  .eyebrow {
    font-family: var(--font-sans);
    font-size: var(--text-xs);
    font-weight: var(--weight-medium);
    color: var(--ink-muted);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin-bottom: var(--space-sm);
  }
  .summary {
    font-family: var(--font-serif);
    font-style: italic;
    font-size: var(--text-lg);
    color: var(--ink-muted);
    line-height: var(--leading-snug);
    margin-top: var(--space-sm);
    margin-bottom: var(--space-lg);
  }
  .formula {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: var(--text-lg);
    text-align: center;
    margin: var(--space-lg) 0;
    padding: var(--space-md) var(--space-lg);
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    color: var(--ink);
  }

  .panels {
    width: 100%;
    max-width: 880px;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: var(--space-md);
    margin: var(--space-2xl) 0;
  }
  .panel {
    display: flex;
    flex-direction: column;
    padding: var(--space-md);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    background: var(--surface);
  }
  .panel-header {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: var(--space-sm);
  }
  .size {
    font-family: var(--font-sans);
    font-weight: var(--weight-medium);
    font-size: var(--text-sm);
    color: var(--ink);
  }
  .regime {
    font-family: var(--font-sans);
    font-size: var(--text-xs);
    color: var(--ink-muted);
    font-style: italic;
  }
  .problem {
    margin-bottom: var(--space-md);
    padding: var(--space-sm);
    background: var(--bg);
    border-radius: var(--radius-sm);
  }
  .operands {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 11px;
    color: var(--ink);
    word-break: break-all;
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    align-items: baseline;
  }
  .times {
    color: var(--ink-muted);
    padding: 0 2px;
  }

  .grid {
    display: flex;
    flex-wrap: wrap;
    gap: 3px;
    margin-bottom: var(--space-md);
    min-height: 16px;
  }
  .dot {
    width: 10px;
    height: 10px;
    border-radius: 2px;
    display: inline-block;
  }
  .dot.ok { background: var(--viz-success); }
  .dot.err { background: var(--viz-error); }

  .stats {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: var(--space-md);
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 12px;
  }
  .stat {
    display: grid;
    grid-template-columns: 60px 80px 1fr;
    gap: 6px;
    align-items: baseline;
  }
  .stat .k {
    color: var(--ink-muted);
  }
  .stat .v {
    color: var(--ink);
    font-weight: var(--weight-medium);
  }
  .stat .d {
    color: var(--ink-subtle);
    font-size: 11px;
  }

  .outcome {
    margin-top: auto;
    padding: var(--space-sm);
    border-radius: var(--radius-sm);
    text-align: center;
    font-family: var(--font-sans);
    font-size: var(--text-sm);
    font-weight: var(--weight-medium);
  }
  .outcome.ok {
    background: rgba(47, 168, 90, 0.12);
    color: var(--viz-success-ink);
  }
  .outcome.err {
    background: rgba(217, 76, 76, 0.12);
    color: var(--viz-error-ink);
  }

  @media (max-width: 900px) {
    .panels {
      grid-template-columns: 1fr;
    }
    .training-origins {
      grid-template-columns: 1fr;
    }
  }

  .subhead {
    font-family: var(--font-sans);
    font-size: var(--text-base);
    font-weight: var(--weight-medium);
    color: var(--ink);
    margin-top: var(--space-xl);
    margin-bottom: var(--space-sm);
  }

  /* ── Visualization: what training gives us ───────────────────────── */
  .training-origins {
    width: 100%;
    max-width: 880px;
    display: flex;
    justify-content: center;
    margin: var(--space-lg) 0 var(--space-xl);
  }
  .training-origins .origin-card {
    max-width: 520px;
    width: 100%;
  }
  .origin-card {
    padding: var(--space-md);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    background: var(--surface);
    display: flex;
    flex-direction: column;
    gap: var(--space-sm);
  }
  .origin-label {
    font-family: var(--font-sans);
    font-size: var(--text-xs);
    font-weight: var(--weight-medium);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--ink-muted);
  }
  .mini-tableau {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 11px;
    line-height: 1.35;
    color: var(--ink);
    margin: 0;
    padding: var(--space-sm);
    background: var(--bg);
    border-radius: var(--radius-sm);
    white-space: pre;
    overflow-x: auto;
  }
  .tableau {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 12px;
    line-height: 1.5;
    color: var(--ink);
    padding: var(--space-md);
    background: var(--bg);
    border-radius: var(--radius-sm);
    overflow-x: auto;
  }
  .tableau .tab-row {
    white-space: pre;
  }
  .tableau .scaffold {
    color: var(--viz-accent);
  }
  .tableau .value {
    color: var(--viz-success-text);
    font-weight: var(--weight-medium);
  }
  .dual-swatch {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 2px;
    vertical-align: -1px;
    flex-shrink: 0;
  }
  .dual-swatch.scaffold-swatch { background: var(--viz-accent); }
  .dual-swatch.value-swatch { background: var(--viz-success-text); }
  .origin-note {
    font-family: var(--font-sans);
    font-size: var(--text-xs);
    color: var(--ink-muted);
    line-height: var(--leading-snug);
  }
  .dual-caption {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: var(--space-xs);
  }
  .dual-item {
    display: grid;
    grid-template-columns: 16px 90px 1fr;
    gap: var(--space-sm);
    align-items: baseline;
    font-family: var(--font-sans);
    font-size: var(--text-sm);
    line-height: var(--leading-snug);
  }
  .dual-key {
    font-weight: var(--weight-medium);
    color: var(--ink);
    font-family: var(--font-sans);
  }
  .dual-val {
    color: var(--ink-muted);
  }
  .dual-note {
    margin-top: 4px;
    font-family: var(--font-sans);
    font-size: var(--text-xs);
    color: var(--ink-subtle);
    font-style: italic;
  }
</style>
